import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { enqueuePhotos } from '../lib/photoUploader';   // R116 #5: claim evidence (photos + clips) rides the persisted background queue
import { mediaStrip, bindMediaStrips, toMediaItems, mergeLandedMedia, EVIDENCE_ACCEPT, pickSummary } from '../shared/lib/media';

// R50 — Damages & Claims: internal damage claims recovered against the damage
// waivers guests pay. Lifecycle open → approved/denied → recovered, per-claim
// supply re-order + photo evidence, and recovered claims mirror into the
// Resolutions ledger. Internal only — never touches external owner statements.
// Backed by GET/POST /api/assetops/claims*.
//
// R111: lives inside the Remote Operations hub (the hub prints the intro, so
// this keeps its compact h2). Drawn on main.css tokens + shared classes —
// .kpi-card band, .chip filters, .input fields, .pill statuses/channels,
// .btn-primary — no bare hex, no var(--x, #hex) fallbacks.
const API = '/api/assetops';
const ST_LABEL: Record<string, string> = { open: 'Open', approved: 'Approved', denied: 'Denied', recovered: 'Recovered' };
// status → main.css .pill variant (denied is also struck through)
const ST_PILL: Record<string, string> = { open: 'pill-amber', approved: 'pill-blue', denied: 'pill-gray clm-struck', recovered: 'pill-green' };
// source chip from the source_ref prefix (log_/turn_/insp_/inc_… else manual)
const SRC_LABEL: Record<string, string> = { log: 'Quick log', turn: 'Turn', insp: 'Inspection', inc: 'Incident', manual: 'Manual' };
// R81 (Pearl): recovery-channel tags.
const CHAN_LABEL: Record<string, string> = { waiver: 'Waiver', waivo: 'Waivo', airbnb: 'Airbnb Claim', other: 'Other' };
const CHAN_PILL: Record<string, string> = { waiver: 'pill-purple', waivo: 'pill-green', airbnb: 'pill-red', other: 'pill-gray' };

export class ClaimsPage {
  private container: HTMLElement;
  private claims: any[] | null = null;      // null = load failed
  private listings: any[] = [];             // /api/listings/active for the picker
  private fStatus = 'open';                 // R50: status chip filter, client-side redraw only
  private fChannel = 'all';                 // R81: recovery-channel filter (all|waiver|waivo|airbnb|other|untagged)
  private biOpen = true;                    // R81: insights section collapsed state
  private formOpen = false;                 // R50: new-claim form collapsed state
  private form = { listing: '', title: '', note: '', amount: '', guest: '' }; // R50: survives draw()
  private note = '';                        // action feedback banner
  private busy = false;                     // gates double-submits

  constructor(container: HTMLElement) {
    this.container = container;
    // R116 #5: a claim's photo / clip landed in the background → refresh that card's record
    const onUp = (e: Event) => {
      if (!this.container.isConnected) { window.removeEventListener('surge-photo-uploaded', onUp); return; }   // a re-rendered hub made a new page
      const d = (e as CustomEvent).detail || {};
      if (!/\/claims\/[^/]+\/photo$/.test(String(d.url || ''))) return;
      if (d.response && d.response.claim) { const mine = (this.claims || []).find((c: any) => c.id === d.response.claim.id); this.swap(mine ? mergeLandedMedia({ ...mine }, d.response.claim) : d.response.claim); this.draw(); }
    };
    window.addEventListener('surge-photo-uploaded', onUp);
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div class="page-content"><div id="clm-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    const [c, l] = await Promise.all([
      fetch(`${API}/claims`).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/listings/active?include=onboarding')   /* R82: EVERY non-inactive listing — resolutions/claims can hit homes with no recent bookings */.then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    this.claims = c ? (c.claims || []) : null;
    this.listings = l ? (Array.isArray(l) ? l : (l.listings || [])) : [];
    this.draw();
  }

  // R111: the hub delegates App's 15-min refresh veto to the mounted page —
  // never wipe a half-typed new claim or a save in flight.
  suspendAutoRefresh(): boolean {
    return this.formOpen || this.busy;
  }

  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private labelOf(l: any): string { return String(l.short_name || l.name || l.id); }
  private money(n: any): string { return `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`; }
  private fmtD(s: string | null): string {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()]} ${d.getUTCDate()}`;
  }
  private srcOf(ref: any): string {
    const m = /^(log|turn|insp|inc|wo|msg)/.exec(String(ref || ''));
    return m ? m[1] : 'manual';
  }
  private who(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.full_name || p.email || sessionStorage.getItem('userRole') || '').trim(); } catch (_) { return ''; }
  }
  // updated claim from the server replaces the local row in place
  private swap(claim: any): void {
    if (!claim || !this.claims) return;
    const i = this.claims.findIndex(c => c.id === claim.id);
    if (i >= 0) this.claims[i] = claim; else this.claims.unshift(claim);
  }

  // R81 #4 (Madel: "can we have something similar to these dashboards?"):
  // claims BI — monthly volume & dollars, recovery funnel, top properties.
  // Client-computed from the loaded claims (small store); bars are plain CSS.
  private insightsHtml(all: any[]): string {
    if (!all.length) return '';
    const amt = (c: any) => Number(c.amount) || 0;
    const claimed = all.reduce((s2, c) => s2 + amt(c), 0);
    const recovered = all.filter(c => c.status === 'recovered').reduce((s2, c) => s2 + amt(c), 0);
    const denied = all.filter(c => c.status === 'denied').reduce((s2, c) => s2 + amt(c), 0);
    const withAmt = all.filter(c => c.amount != null);
    const avg = withAmt.length ? claimed / withAmt.length : 0;
    const closed = all.filter(c => c.status === 'recovered' || c.status === 'denied');
    const recRate = closed.length ? Math.round(all.filter(c => c.status === 'recovered').length / closed.length * 100) : null;
    // last 12 months
    const months: string[] = [];
    const d = new Date();
    for (let i = 11; i >= 0; i--) {
      const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1));
      months.push(m.toISOString().slice(0, 7));
    }
    const byMo = new Map(months.map(m => [m, { n: 0, sum: 0, rec: 0 }]));
    for (const c of all) {
      const m = String(c.created_at || '').slice(0, 7);
      const b = byMo.get(m);
      if (b) { b.n++; b.sum += amt(c); if (c.status === 'recovered') b.rec += amt(c); }
    }
    const maxSum = Math.max(1, ...[...byMo.values()].map(b => b.sum));
    const bars = months.map(m => {
      const b = byMo.get(m)!;
      const hAll = Math.round(b.sum / maxSum * 72);
      const hRec = b.sum > 0 ? Math.round(b.rec / maxSum * 72) : 0;
      return `<div class="clm-bi-col" title="${m}: ${b.n} claim${b.n === 1 ? '' : 's'} · ${this.money(b.sum)} claimed · ${this.money(b.rec)} recovered">
        <span class="clm-bi-n num">${b.n || ''}</span>
        <div class="clm-bi-stack">
          <div class="clm-bi-all" style="height:${hAll}px;"></div>
          <div class="clm-bi-rec ${hRec >= hAll ? 'top' : ''}" style="height:${hRec}px;"></div>
        </div>
        <span class="clm-bi-m">${m.slice(5)}</span>
      </div>`;
    }).join('');
    const byProp = new Map<string, { n: number; sum: number }>();
    for (const c of all) {
      const k = String(c.listing_name || '—');
      if (!byProp.has(k)) byProp.set(k, { n: 0, sum: 0 });
      const b = byProp.get(k)!; b.n++; b.sum += amt(c);
    }
    const top = [...byProp.entries()].sort((a, b) => b[1].sum - a[1].sum).slice(0, 5);
    const stat = (v: string, l: string) => `<div class="clm-bi-stat"><div class="clm-bi-v num">${v}</div><div class="clm-bi-l">${l}</div></div>`;
    return `
      <div class="clm-bi ${this.biOpen ? 'open' : ''}">
        <div class="clm-bi-head" id="clm-bi-toggle">
          <span class="clm-bi-title">📊 Claims insights</span>
          <span class="clm-bi-sub">${all.length} claims all-time · ${this.money(claimed)} claimed · ${this.money(recovered)} recovered</span>
          <span class="clm-bi-caret">${this.biOpen ? '▾' : '▸'}</span>
        </div>
        ${this.biOpen ? `
        <div class="clm-bi-body">
          <div class="clm-bi-stats">
            ${stat(String(all.length), 'Claims all-time')}
            ${stat(this.money(claimed), 'Claimed $')}
            ${stat(this.money(recovered), 'Recovered $')}
            ${stat(this.money(denied), 'Denied $')}
            ${stat(this.money(avg), 'Avg claim')}
            ${stat(recRate != null ? `${recRate}%` : '—', 'Recovery rate (closed)')}
          </div>
          <div class="clm-bi-lab">Monthly — claimed $ (light) vs recovered $ (green), count on top</div>
          <div class="clm-bi-bars">${bars}</div>
          ${top.length ? `
          <div class="clm-bi-lab mt">Top properties by claimed $</div>
          ${top.map(([name, b]) => `<div class="clm-bi-top"><span class="name">${this.esc(name)}</span><span class="num n">${b.n} claim${b.n === 1 ? '' : 's'}</span><span class="num sum">${this.money(b.sum)}</span></div>`).join('')}` : ''}
        </div>` : ''}
      </div>`;
  }

  // R81 #6 (Michelle's pattern, applied here too): every claim as CSV.
  private exportCsv(): void {
    const all = this.claims || [];
    const esc2 = (v: any) => { const s2 = String(v == null ? '' : v); return /[",\n\r]/.test(s2) ? `"${s2.replace(/"/g, '""')}"` : s2; };
    const head = ['id', 'created_at', 'property', 'title', 'status', 'channel', 'amount', 'guest', 'source', 'created_by', 'resolution_id', 'note'];
    const lines = [head.join(',')];
    for (const c of all) {
      lines.push([c.id, c.created_at, c.listing_name, c.title, c.status, c.claim_channel || '', c.amount != null ? c.amount : '', c.guest, this.srcOf(c.source_ref), c.created_by, c.resolution_id || '', c.note].map(esc2).join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `claims_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  private draw(): void {
    const body = document.getElementById('clm-body');
    if (!body) return;
    if (!this.claims) { body.innerHTML = emptyState('Claims unavailable', 'GET /api/assetops/claims failed — check the server log.', '⚠️'); return; }
    const all = this.claims;
    const n = (st: string) => all.filter(c => c.status === st).length;
    const approvedSum = all.filter(c => c.status === 'approved' || c.status === 'recovered').reduce((s, c) => s + (Number(c.amount) || 0), 0);
    let rows = this.fStatus === 'all' ? all : all.filter(c => c.status === this.fStatus);
    if (this.fChannel !== 'all') rows = rows.filter(c => (this.fChannel === 'untagged' ? !c.claim_channel : c.claim_channel === this.fChannel));

    // Review #9: each chip row counts WITHIN the other filter's selection, so
    // a chip's number always equals the cards a click will show.
    const chanBase = this.fChannel === 'all' ? all : all.filter(c => this.fChannel === 'untagged' ? !c.claim_channel : c.claim_channel === this.fChannel);
    const statBase = this.fStatus === 'all' ? all : all.filter(c => c.status === this.fStatus);
    // R111: main.css .chip (.active = pale-blue) with a .chip-count; data-f/data-fc/data-v unchanged.
    const chip = (v: string, label: string, cnt: number) =>
      `<button type="button" class="chip ${this.fStatus === v ? 'active' : ''}" data-f="1" data-v="${v}">${label} <span class="chip-count">${cnt}</span></button>`;
    // main.css .kpi-card at the Calendar's density
    const kpi = (label: string, ico: string, v: string, cap: string, cls = '') => `
      <div class="kpi-card ${cls}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${v}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;

    const f = this.form;
    const dlOpts = this.listings.map(l => `<option value="${this.esc(this.labelOf(l))}"></option>`).join('');
    const formHtml = `
      <div class="clm-form">
        <div class="clm-form-head">
          <div class="clm-form-title">New claim</div>
          <div class="clm-form-sub">Property and title are required; add the amount once you know it.</div>
        </div>
        <div class="clm-form-grid">
          <label>Property *<input class="input" data-nf="listing" list="clm-props" value="${this.esc(f.listing)}" placeholder="Start typing…" autocomplete="off"><datalist id="clm-props">${dlOpts}</datalist></label>
          <label>Title *<input class="input" data-nf="title" value="${this.esc(f.title)}" placeholder="e.g. Wine stain on the sofa"></label>
          <label>Amount ($)<input class="input" data-nf="amount" type="number" min="0" step="0.01" value="${this.esc(f.amount)}" placeholder="0.00"></label>
          <label>Guest<input class="input" data-nf="guest" value="${this.esc(f.guest)}" placeholder="Guest name"></label>
          <label class="wide">Note<textarea class="input" data-nf="note" rows="2" placeholder="What happened, evidence, waiver context…">${this.esc(f.note)}</textarea></label>
        </div>
        <div class="clm-form-foot"><button type="button" class="btn btn-primary" id="clm-create" ${this.busy ? 'disabled' : ''}>Create claim</button></div>
      </div>`;

    const cardHtml = (c: any) => {
      const src = this.srcOf(c.source_ref);
      // R116 #5: photos + clips (a video used to sit in photos and draw as a broken image) — tap opens the viewer
      const thumbs = mediaStrip(toMediaItems([...(c.photos || []), ...(Array.isArray(c.videos) ? c.videos : [])], c.videos || []), { size: 'sm', alt: 'claim photo' });
      const stBtns = c.status === 'open'
        ? `<button type="button" class="clm-btn ok" data-act="approve">✓ Approve</button><button type="button" class="clm-btn bad" data-act="deny">✕ Deny</button>`
        : c.status === 'approved'
          ? `<button type="button" class="clm-btn ok" data-act="recover">💰 Mark recovered</button><button type="button" class="clm-btn" data-act="reopen">↩ Reopen</button>`
          : `<button type="button" class="clm-btn" data-act="reopen">↩ Reopen</button>`;
      const supplies = c.supply_order_ref
        ? `<span class="clm-done" title="${this.esc(c.supply_order_ref)}">✓ supplies requested</span>`
        : `<button type="button" class="clm-btn" data-act="supplies" title="Create a supplies request to replace the damaged items">📦 Request supplies</button>`;
      const ledger = c.resolution_id
        ? `<span class="clm-done" title="${this.esc(c.resolution_id)}">✓ in ledger</span>`
        : c.status === 'recovered'
          ? `<button type="button" class="clm-btn led" data-act="ledger" title="Mirror the recovered amount as a settled charge in the Resolutions ledger">Log in Resolutions ledger →</button>`
          : '';
      return `
        <div class="clm-card" data-id="${this.esc(c.id)}">
          <div class="clm-card-top">
            <span class="clm-src">${this.esc(SRC_LABEL[src] || src)}</span>
            <span class="pill ${ST_PILL[c.status] || 'pill-gray'}">${this.esc(ST_LABEL[c.status] || c.status)}</span>
            ${c.claim_channel ? `<span class="pill ${CHAN_PILL[c.claim_channel] || 'pill-gray'}">${this.esc(CHAN_LABEL[c.claim_channel] || c.claim_channel)}</span>` : ''}
            ${c.amount != null ? `<span class="clm-amt-tag num">${this.money(c.amount)}</span>` : ''}
            <select class="input clm-chan" data-role="clm-channel" title="Where this claim is being recovered — Waiver (guest damage waiver), Waivo insurance, or an Airbnb claim">
              <option value="">tag…</option>
              ${Object.entries(CHAN_LABEL).map(([v, l]) => `<option value="${v}" ${c.claim_channel === v ? 'selected' : ''}>${l}</option>`).join('')}
              ${c.claim_channel ? '<option value="__clear">✕ clear tag</option>' : ''}
            </select>
          </div>
          <div class="clm-title">${this.esc(c.title)}</div>
          <div class="clm-meta">${this.esc(c.listing_name || '—')} · ${this.fmtD(c.created_at)}${c.guest ? ` · guest ${this.esc(c.guest)}` : ''}</div>
          ${thumbs}
          <div class="clm-noterow">
            <textarea class="input" data-role="clm-note" rows="2" placeholder="Note…">${this.esc(c.note || '')}</textarea>
            <button type="button" class="clm-btn" data-act="save-note" title="Save note">💾</button>
          </div>
          <div class="clm-amtrow">
            <span class="clm-amt-lab">$</span>
            <input class="input clm-amt-in" data-role="clm-amount" type="number" min="0" step="0.01" value="${c.amount != null ? this.esc(c.amount) : ''}" placeholder="0.00">
            <button type="button" class="clm-btn" data-act="save-amount">Save $</button>
          </div>
          <div class="clm-actions">
            ${stBtns}
            <button type="button" class="clm-btn" data-act="photo" title="Attach photos or a video — several at once">＋📷${(c.photos || []).length + (Array.isArray(c.videos) ? c.videos.length : 0) ? ` ${(c.photos || []).length + (Array.isArray(c.videos) ? c.videos.length : 0)}` : ''}</button>
            <input type="file" accept="${EVIDENCE_ACCEPT}" multiple data-role="clm-photo" style="display:none;">
            ${supplies}
            ${ledger}
          </div>
        </div>`;
    };

    body.innerHTML = `
      <style>
        .clm-head { display:flex; align-items:baseline; gap:12px; margin-bottom:4px; flex-wrap:wrap; }
        .clm-head h2 { margin:0; font-size:16px; font-weight:600; letter-spacing:-0.01em; color:var(--text); }
        .clm-head .sub { font-size:12px; color:var(--text-muted); }
        .clm-head .btn { margin-left:auto; }
        .clm-basis { font-size:11.5px; color:var(--text-faint); margin:4px 0 14px; }
        .clm-banner { font-size:12px; color:var(--success-text); background:var(--success-soft); border:1px solid var(--success-border); border-radius:var(--radius-sm); padding:8px 12px; margin-bottom:12px; }
        /* KPI band at the Calendar's density */
        .kpi-grid.clm-kpis { grid-template-columns:repeat(5, 1fr); gap:12px; margin-bottom:14px; }
        .clm-kpis .kpi-card { padding:14px 16px; }
        .clm-kpis .kpi-card-value { font-size:24px; margin-top:10px; }
        .clm-kpis .kpi-card-ic { width:34px; height:34px; border-radius:10px; }
        .clm-kpis .kpi-card-cap { margin-top:4px; }
        /* Filters */
        .clm-filters { display:flex; gap:6px; flex-wrap:wrap; align-items:center; margin-bottom:12px; }
        .clm-filters .chip { padding:5px 11px; font-size:11.5px; }
        .clm-gap { width:8px; }
        .clm-filters .clm-csv { margin-left:auto; }
        /* Claim cards */
        .clm-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:12px; }
        .clm-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); padding:14px 16px; box-shadow:var(--shadow-card); display:flex; flex-direction:column; gap:8px; }
        .clm-card-top { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .clm-src { font-size:10.5px; font-weight:600; padding:2px 8px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); white-space:nowrap; }
        .clm-struck { text-decoration:line-through; }
        .clm-amt-tag { margin-left:auto; font-size:13px; font-weight:700; }
        .clm-chan.input { width:auto; font-size:11px; padding:4px 8px; margin-left:auto; }
        .clm-amt-tag ~ .clm-chan.input { margin-left:0; }   /* the amount already pushed to the right edge */
        .clm-title { font-size:13.5px; font-weight:600; line-height:1.35; color:var(--text); }
        .clm-meta { font-size:11.5px; color:var(--text-muted); }
        .clm-thumbs { display:flex; gap:5px; flex-wrap:wrap; }
        .clm-thumb { width:56px; height:56px; object-fit:cover; border-radius:var(--radius-sm); border:1px solid var(--border); display:block; }
        .clm-card .input { font-size:12.5px; padding:7px 10px; }
        .clm-noterow { display:flex; gap:6px; align-items:flex-start; }
        .clm-noterow textarea { flex:1; resize:vertical; }
        .clm-amtrow { display:flex; gap:6px; align-items:center; }
        .clm-amt-lab { font-size:12px; font-weight:600; color:var(--text-muted); }
        .clm-amt-in.input { width:120px; }
        .clm-actions { display:flex; gap:5px; align-items:center; flex-wrap:wrap; border-top:1px solid var(--border-soft); padding-top:9px; margin-top:auto; }
        .clm-btn { font-size:10.5px; font-weight:600; padding:4px 9px; border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--card); color:var(--text-muted); cursor:pointer; white-space:nowrap; font-family:inherit; transition:border-color .12s ease, background .12s ease, color .12s ease; }
        .clm-btn:hover { border-color:var(--border-strong); background:var(--bg-subtle); color:var(--text); }
        .clm-btn:disabled { opacity:.5; cursor:default; }
        .clm-btn.ok { border-color:var(--success-border); color:var(--success-text); background:var(--success-soft); }
        .clm-btn.ok:hover { border-color:var(--success); }
        .clm-btn.bad { border-color:var(--danger-border); color:var(--danger-text); }
        .clm-btn.bad:hover { background:var(--danger-soft); border-color:var(--danger); }
        .clm-btn.led { border-color:var(--accent-border); color:var(--accent); background:var(--accent-soft); }
        .clm-btn.led:hover { border-color:var(--accent); }
        .clm-done { font-size:10.5px; font-weight:700; color:var(--success-text); white-space:nowrap; }
        /* New-claim form: card-head / card-foot chrome */
        .clm-form { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); margin-bottom:14px; overflow:hidden; }
        .clm-form-head { padding:12px 16px; border-bottom:1px solid var(--border); background:var(--bg-subtle); }
        .clm-form-title { font-size:13px; font-weight:700; color:var(--text); }
        .clm-form-sub { font-size:11.5px; color:var(--text-muted); margin-top:2px; }
        .clm-form-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(180px, 1fr)); gap:10px 14px; padding:14px 16px; }
        .clm-form-grid label { display:flex; flex-direction:column; gap:5px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .clm-form-grid label.wide { grid-column:1 / -1; }
        .clm-form-grid .input { text-transform:none; letter-spacing:0; font-weight:400; font-size:12.5px; }
        .clm-form-foot { display:flex; justify-content:flex-end; gap:8px; padding:12px 16px; border-top:1px solid var(--border); background:var(--bg-subtle); }
        .clm-empty { border:1px dashed var(--border); border-radius:var(--radius); background:var(--card); padding:28px; text-align:center; color:var(--text-muted); font-size:12.5px; }
        /* Insights card */
        .clm-bi { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); margin-bottom:14px; overflow:hidden; }
        .clm-bi-head { cursor:pointer; display:flex; gap:10px; align-items:center; padding:11px 16px; flex-wrap:wrap; }
        .clm-bi.open .clm-bi-head { border-bottom:1px solid var(--border); background:var(--bg-subtle); }
        .clm-bi-title { font-size:13px; font-weight:700; color:var(--text); }
        .clm-bi-sub { font-size:11.5px; color:var(--text-muted); }
        .clm-bi-caret { margin-left:auto; color:var(--text-muted); }
        .clm-bi-body { padding:14px 16px; }
        .clm-bi-stats { display:flex; gap:18px; flex-wrap:wrap; margin-bottom:12px; }
        .clm-bi-stat { min-width:110px; }
        .clm-bi-v { font-size:19px; font-weight:700; letter-spacing:-0.02em; color:var(--text); }
        .clm-bi-l { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
        .clm-bi-lab { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin-bottom:4px; }
        .clm-bi-lab.mt { margin-top:12px; }
        .clm-bi-bars { display:flex; gap:4px; align-items:flex-end; overflow-x:auto; padding-bottom:2px; }
        .clm-bi-col { flex:1; display:flex; flex-direction:column; align-items:center; gap:3px; min-width:34px; }
        .clm-bi-n { font-size:10px; color:var(--text-muted); min-height:12px; }
        .clm-bi-stack { position:relative; width:70%; height:72px; display:flex; align-items:flex-end; justify-content:center; }
        .clm-bi-all { position:absolute; bottom:0; width:100%; background:var(--accent-soft); border-radius:4px 4px 0 0; }
        .clm-bi-rec { position:absolute; bottom:0; width:100%; background:var(--success); opacity:.75; }
        .clm-bi-rec.top { border-radius:4px 4px 0 0; }
        .clm-bi-m { font-size:9.5px; color:var(--text-faint); }
        .clm-bi-top { display:flex; gap:8px; font-size:12.5px; padding:2px 0; }
        .clm-bi-top .name { flex:1; }
        .clm-bi-top .n { color:var(--text-muted); }
        .clm-bi-top .sum { font-weight:700; }
        @media (max-width: 1100px) {
          .kpi-grid.clm-kpis { grid-template-columns:repeat(3, 1fr); }
        }
        @media (max-width: 720px) {
          .kpi-grid.clm-kpis { grid-template-columns:repeat(2, 1fr); gap:10px; }
          .clm-filters .clm-csv { margin-left:0; }
          .clm-head .sub { flex-basis:100%; order:3; }   /* title + button share the first line; the purpose drops below */
        }
      </style>
      <div class="clm-head">
        <h2>Damages & Claims</h2>
        <span class="sub">the damage-recovery pipeline — log it, approve it, collect it</span>
        <button type="button" class="btn btn-primary btn-sm" id="clm-new-toggle">${this.formOpen ? '✕ Close' : '＋ New claim'}</button>
      </div>
      <div class="clm-basis">Internal damage claims — recovered against the damage waivers guests pay. Internal only, never on owner statements.</div>
      ${this.note ? `<div class="clm-banner">${this.esc(this.note)}</div>` : ''}
      ${this.formOpen ? formHtml : ''}
      <div class="kpi-grid clm-kpis">
        ${kpi('Open', 'flag', String(n('open')), 'awaiting a decision', n('open') > 0 ? 'tone-warn' : '')}
        ${kpi('Approved', 'target', String(n('approved')), 'ready to collect')}
        ${kpi('Denied', 'file', String(n('denied')), 'closed, nothing recovered')}
        ${kpi('Recovered', 'wallet', String(n('recovered')), 'money in hand', n('recovered') > 0 ? 'tone-good' : '')}
        ${kpi('Approved $', 'receipt', this.money(approvedSum), 'approved + recovered claims')}
      </div>
      ${this.insightsHtml(all)}
      <div class="clm-filters">
        ${chip('all', 'All', chanBase.length)}${chip('open', 'Open', chanBase.filter(c => c.status === 'open').length)}${chip('approved', 'Approved', chanBase.filter(c => c.status === 'approved').length)}${chip('denied', 'Denied', chanBase.filter(c => c.status === 'denied').length)}${chip('recovered', 'Recovered', chanBase.filter(c => c.status === 'recovered').length)}
        <span class="clm-gap"></span>
        ${(['all', 'waiver', 'waivo', 'airbnb', 'other', 'untagged'] as const).map(v => {
          const cnt = v === 'all' ? statBase.length : statBase.filter(c => v === 'untagged' ? !c.claim_channel : c.claim_channel === v).length;
          return `<button type="button" class="chip ${this.fChannel === v ? 'active' : ''}" data-fc="1" data-v="${v}">${v === 'all' ? 'Any channel' : (CHAN_LABEL[v] || 'Untagged')} <span class="chip-count">${cnt}</span></button>`;
        }).join('')}
        <button type="button" class="btn btn-secondary btn-sm clm-csv" id="clm-csv" title="Download every claim (all statuses) as a CSV">⬇ Export CSV</button>
      </div>
      ${rows.length
        ? `<div class="clm-grid">${rows.map(cardHtml).join('')}</div>`
        : `<div class="clm-empty">No ${this.fStatus === 'all' ? '' : `${this.fStatus} `}claims${this.fChannel !== 'all' ? ` tagged ${this.fChannel === 'untagged' ? 'nothing' : (CHAN_LABEL[this.fChannel] || this.fChannel)}` : ''}.</div>`}`;
    this.bind();
  }

  private bind(): void {
    bindMediaStrips(this.container);   // R116 #5
    this.container.querySelectorAll<HTMLElement>('[data-f]').forEach(el => el.addEventListener('click', () => {
      this.fStatus = el.dataset.v!; this.draw();
    }));
    this.container.querySelectorAll<HTMLElement>('[data-fc]').forEach(el => el.addEventListener('click', () => {
      this.fChannel = el.dataset.v!; this.draw();
    }));
    this.container.querySelector('#clm-bi-toggle')?.addEventListener('click', () => { this.biOpen = !this.biOpen; this.draw(); });
    this.container.querySelector('#clm-csv')?.addEventListener('click', () => this.exportCsv());
    this.container.querySelector<HTMLButtonElement>('#clm-new-toggle')?.addEventListener('click', () => {
      this.formOpen = !this.formOpen; this.draw();
    });
    // new-claim inputs sync to class fields so a redraw never loses typed state
    this.container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-nf]').forEach(el =>
      el.addEventListener('input', () => { (this.form as any)[el.dataset.nf!] = el.value; }));
    this.container.querySelector<HTMLButtonElement>('#clm-create')?.addEventListener('click', () => void this.create());

    this.container.querySelectorAll<HTMLElement>('.clm-card[data-id]').forEach(card => {
      const id = card.dataset.id!;
      const c = (this.claims || []).find((x: any) => x.id === id);
      if (!c) return;
      const t = String(c.title || '').slice(0, 60);
      card.querySelector('[data-act="approve"]')?.addEventListener('click', () => this.setStatus(c, 'approved', `Approve claim "${t}"?`));
      card.querySelector('[data-act="deny"]')?.addEventListener('click', () => this.setStatus(c, 'denied', `Deny claim "${t}"?`));
      card.querySelector('[data-act="recover"]')?.addEventListener('click', () => this.setStatus(c, 'recovered', `Mark "${t}" recovered — the money is in hand?`));
      card.querySelector('[data-act="reopen"]')?.addEventListener('click', () => this.setStatus(c, 'open', `Reopen claim "${t}"?`));
      card.querySelector('[data-role="clm-channel"]')?.addEventListener('change', (e) => {
        const v = (e.currentTarget as HTMLSelectElement).value;
        if (!v) return;   // 'tag…' placeholder
        void this.post(id, { claim_channel: v === '__clear' ? null : v }, v === '__clear' ? 'Tag cleared.' : `Tagged ${CHAN_LABEL[v] || v}.`);
      });
      card.querySelector('[data-act="supplies"]')?.addEventListener('click', () => void this.routeSupplies(c));
      card.querySelector('[data-act="ledger"]')?.addEventListener('click', () => void this.logLedger(c));
      card.querySelector('[data-act="save-note"]')?.addEventListener('click', () => {
        const v = card.querySelector<HTMLTextAreaElement>('[data-role="clm-note"]')?.value ?? '';
        void this.post(id, { note: v.trim() }, 'Note saved.');
      });
      card.querySelector('[data-act="save-amount"]')?.addEventListener('click', () => {
        const raw = (card.querySelector<HTMLInputElement>('[data-role="clm-amount"]')?.value || '').trim();
        if (!raw) { void this.post(id, { amount: null }, 'Amount cleared.'); return; }
        const amount = Number(raw);
        if (!Number.isFinite(amount) || amount < 0) { alert('Amount must be a positive number.'); return; }
        void this.post(id, { amount }, `Amount set to ${this.money(amount)}.`);
      });
      const inp = card.querySelector<HTMLInputElement>('input[data-role="clm-photo"]');
      card.querySelector('[data-act="photo"]')?.addEventListener('click', () => { if (inp && !inp.disabled) inp.click(); });
      inp?.addEventListener('change', () => void this.uploadPhotos(c, inp));
    });
  }

  // shared claim-update POST; server logs each change and returns the claim
  private async post(id: string, body: any, okNote: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const r = await fetch(`${API}/claims/${encodeURIComponent(id)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, by: this.who() || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.swap(j.claim);
      this.note = okNote;
    } catch (e: any) { this.note = `Update failed: ${e.message}`; }
    this.busy = false;
    this.draw();
  }

  private setStatus(c: any, status: string, msg: string): void {
    if (!confirm(msg)) return;
    void this.post(c.id, { status }, `Claim ${status === 'open' ? 'reopened' : status}.`);
  }

  // idempotent server-side — safe to re-hit; disabled once supply_order_ref set
  private async routeSupplies(c: any): Promise<void> {
    if (!confirm(`Request replacement supplies for "${String(c.title || '').slice(0, 60)}"?`)) return;
    if (this.busy) return;
    this.busy = true;
    try {
      const r = await fetch(`${API}/claims/${encodeURIComponent(c.id)}/route-supplies`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ by: this.who() || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      this.swap(j.claim);
      this.note = `Supplies requested${j.order && j.order.id ? ` — order ${j.order.id}` : ''}.`;
    } catch (e: any) { this.note = `Supplies request failed: ${e.message}`; }
    this.busy = false;
    this.draw();
  }

  // R116 #5: every picked photo / clip → the persisted background queue (one per
  // request; the card refreshes as each lands — see the constructor's listener).
  private async uploadPhotos(c: any, inp: HTMLInputElement): Promise<void> {
    const files = Array.from(inp.files || []);
    inp.value = '';
    if (!files.length || inp.disabled) return;
    inp.disabled = true;   // double-pick guard
    try {
      const n = await enqueuePhotos(`${API}/claims/${encodeURIComponent(c.id)}/photo`, files, {}, 'claim photo');
      this.note = n ? `${pickSummary(files)} queued — uploading in the background.` : 'Those files could not be queued — too large? Try a shorter clip.';
    } catch (e: any) { this.note = `Couldn't queue the files: ${e.message}`; }
    finally { inp.disabled = false; }
    this.draw();
  }

  // recovered claim → settled "charge" row in the Resolutions ledger (role-gated
  // server-side), then stamp resolution_id back on the claim so it shows ✓.
  private ledgerLogged = new Set<string>();   // R50 review: created-but-unstamped guard (session-local)
  private async logLedger(c: any): Promise<void> {
    const amount = Number(c.amount);
    if (c.resolution_id) { alert('Already logged in the Resolutions ledger.'); return; }
    // A prior attempt CREATED the ledger row but failed to stamp the claim —
    // re-clicking would double-charge the ledger. Session guard + honest alert.
    if (this.ledgerLogged.has(c.id)) { alert('A ledger entry was already created for this claim (the link-back failed). Check the Resolutions page before logging again.'); return; }
    if (!Number.isFinite(amount) || amount <= 0) { alert('Set the recovered $ amount on the claim first — the ledger entry needs it.'); return; }
    if (!confirm(`Log ${this.money(amount)} recovered on "${String(c.title || '').slice(0, 60)}" as a settled charge in the Resolutions ledger?`)) return;
    if (this.busy) return;
    this.busy = true;
    try {
      const r = await fetch('/api/resolutions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: c.listing_id, listing_name: c.listing_name, direction: 'charge', amount,
          reason: `Damage claim: ${c.title}`, reason_code: 'damage', status: 'settled',   // R100 A7: damages paid by the guest are non-taxable, non-commissionable
          date: new Date().toLocaleDateString('en-CA'),   // local YYYY-MM-DD
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const rid = j.id || (j.resolution && j.resolution.id) || (j.record && j.record.id);
      if (!rid) throw new Error('no resolution id returned');
      this.ledgerLogged.add(c.id);          // ledger row EXISTS from here on
      this.busy = false;                    // post() gates on busy
      await this.post(c.id, { resolution_id: rid }, '✓ Logged in the Resolutions ledger.');
      return;
    } catch (e: any) { this.note = `Ledger log failed: ${e.message}`; }
    this.busy = false;
    this.draw();
  }

  private async create(): Promise<void> {
    const f = this.form;
    const title = f.title.trim();
    if (!title) { alert('Give the claim a title.'); return; }
    const typed = f.listing.trim();
    const match = this.listings.find(l => this.labelOf(l).toLowerCase() === typed.toLowerCase());
    if (!match) { alert('Pick a property from the list.'); return; }
    let amount: number | undefined;
    if (f.amount.trim()) {
      amount = Number(f.amount);
      if (!Number.isFinite(amount) || amount < 0) { alert('Amount must be a positive number.'); return; }
    }
    if (this.busy) return;
    this.busy = true;
    try {
      const r = await fetch(`${API}/claims`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: match.id, listing_name: this.labelOf(match), title,
          note: f.note.trim() || undefined, amount, guest: f.guest.trim() || undefined,
          by: this.who() || undefined,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.claim) { if (this.claims) this.claims.unshift(j.claim); else this.claims = [j.claim]; }
      this.form = { listing: '', title: '', note: '', amount: '', guest: '' };
      this.formOpen = false;
      this.fStatus = 'open';                // new claims are open — land the user on it
      this.note = `Claim "${title.slice(0, 50)}" created.`;
    } catch (e: any) { this.note = `Create failed: ${e.message}`; }
    this.busy = false;
    this.draw();
  }
}
